import { FC, useId } from 'react'
import styled, { css } from 'styled-components'

import { useLmfAnalyticsContext } from '@syconium/little-miss-figgy'

import { useTranslation } from '../../../../../../app/_providers/TranslationProvider.client'
import { useEmbroideryGarment } from '../../../../../../containers/EmbroideryContainer/providers/EmbroideryGarmentProvider'
import { EmbroideryIcon } from '../../../../../../containers/EmbroideryContainer/useEmbroideryDesigns'
import { EmbroideryIconGroupSection } from '../../../primitives/EmbroideryIconGroupSection/EmbroideryIconGroupSection'
import { Label, SelectedOptionText } from '../../../primitives/shared-form-styles'
import { ValidationMessage } from '../../../primitives/ValidationMessage/ValidationMessage'

export type IconPickerProps = {
  selectedIcon: EmbroideryIcon | undefined
  onSelectIcon(icon: EmbroideryIcon): void
  errorMessage?: string
}

/**
 * The icons this garment offers, grouped as the catalog groups them, with the chosen one named
 * beside the heading.
 */
export const IconPicker: FC<IconPickerProps> = ({ selectedIcon, onSelectIcon, errorMessage }) => {
  const { designs, productColorHex } = useEmbroideryGarment()
  const labelId = useId()
  const errorId = useId()
  const {
    embroidery: {
      modal: {
        forms: { iconOption },
      },
    },
  } = useTranslation()
  const { track, getContextChain } = useLmfAnalyticsContext()

  const selectIcon = (icon: EmbroideryIcon) => {
    track({
      action: 'click',
      element: 'button',
      variant: 'icon',
      label: icon.key,
      context: getContextChain(),
      href: undefined,
    })
    onSelectIcon(icon)
  }

  return (
    <div>
      <Label id={labelId}>
        {iconOption.iconSelectorLabel}{' '}
        <SelectedOptionText>{selectedIcon?.image.label}</SelectedOptionText>
      </Label>

      <IconGroups
        role='group'
        aria-labelledby={labelId}
        aria-describedby={errorMessage === undefined ? undefined : errorId}
        $invalid={errorMessage !== undefined}
      >
        {designs.iconGroups.map(group => (
          <EmbroideryIconGroupSection
            key={group.id}
            group={group}
            onSelectIcon={selectIcon}
            productColorHex={productColorHex}
            selectedIcon={selectedIcon}
          />
        ))}
      </IconGroups>

      <ValidationMessage id={errorId} message={errorMessage} />
    </div>
  )
}

const IconGroups = styled.div<{ $invalid: boolean }>`
  ${o =>
    o.$invalid
      ? css`
          button[role='radio'] {
            border-color: ${o.theme.color.text.critical.on.background};
          }
        `
      : css``}
`
